import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getToken } from '@app/helpers/authProvider';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ENV } from "@app/constants/env";

const rawBaseQuery = fetchBaseQuery({
    baseUrl: ENV.BASE_API_URL +ENV.JOB_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    incomingArgs,
    api,
    extraOptions
) => {
    let args: string | FetchArgs = incomingArgs;

    if (typeof args === 'string') {
        args = { url: args };
    }

    try {
        const token = await getToken();
        if (token) {
            const headers =
                (args as FetchArgs).headers instanceof Headers
                    ? (args as FetchArgs).headers
                    : new Headers((args as FetchArgs).headers);

            headers.set('Authorization', `Bearer ${token}`);
            (args as FetchArgs).headers = headers;
        }

        const result = await rawBaseQuery(args, api, extraOptions);
        return result;
    } catch (error) {
        return {
            error: {
                status: 'CUSTOM_ERROR',
                error: String(error),
                data: undefined,
            } as FetchBaseQueryError,
        };
    }
};

// Charge Line Types
export interface ChargeLineEntity {
  ChargeLine_Id?: number;
  WoService_Id: number;
  Type?: number;
  ITEMTYPE_ID: number;
  ChargeCode_Id: number;
  LineDescription: string;
  Status?: number;
  StatusName?: string;
  Quantity: number;
  Rate: number;
  Currency?: string;
  EstimatedAmount: number;
  ActualNetAmount: number;
  VATtaxCode?: string;
  VATRate: number;
  VATAmount: number;
  Mstr_Country_Id?: number;
  FromCountry?: number;
  ToCountry?: number;
  RecoginitionDate?: string | null;
  Supplier?: number;
  Sno?: number;
  Invoice_Id?: number;
  Code?: string;
  Customer?: number;
  Line_Id?: number;
  Mark_For_Delete?: boolean;
  TotalSales?: number;
  TotalCosts?: number;
  ServiceType?: number;
  SupplierName?: string;
  Mstr_VATTaxID?: number;
  Createduser?: number;
  TotalGrossAmount?: number;
}

export const chargeLineAPI = createApi({
  reducerPath: 'chargeLineAPI',
  baseQuery: authBaseQuery,
  tagTypes: ['ChargeLine'],
  endpoints: (builder) => ({
    getChargeLinesByServiceId: builder.query<any, number>({
      query: (woServiceId) => ({
        url: `/WosChargeLine/GetChargeLinesByServiceId/${woServiceId}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        const data = response?.data || [];
        // Transform lowercase API field names to PascalCase for the UI
        const transformed = data.map((item: any) => {
          const result = {
            Sno: item.sno,
            WoService_Id: item.woservice_id,
            Type: item.type,
            ChargeLine_Id: item.chargeline_id,
            ChargeCode_Id: item.chargecode_id,
            LineDescription: item.linedescription,
            Status: item.status,
            Rate: item.rate,
            EstimatedAmount: item.estimatedamount,
            ActualNetAmount: item.actualnetamount,
            VATtaxCode: item.vattaxcode,
            VATRate: item.vatrate,
            VATAmount: item.vatamount,
            FromCountry: item.fromcountry,
            ToCountry: item.tocountry,
            RecoginitionDate: item.recoginitiondate,
            Invoice_Id: item.invoice_id,
            Create_Date: item.create_date,
            Edit_Date: item.edit_date,
            Create_User: item.create_user,
            Edit_User: item.edit_user,
            Line_Id: item.line_id,
            Mark_For_Delete: item.mark_for_delete,
            Customer: item.customer,
            Supplier: item.supplier,
            Code: item.code,
            Currency: item.currency,
            Quantity: item.quantity,
            ServiceType: item.servicetype,
            LineOrder: item.lineorder,
            IsExported: item.isexported,
            ExportedOn: item.exportedon,
            ExportedBy: item.exportedby,
            IsAccrualsExported: item.isaccrualsexported,
            Accruals_ExportedOn: item.accruals_exportedon,
            Accruals_ExportedBy: item.accruals_exportedby,
            LocalCurrency: item.localcurrency,
            Total_Costs: item.total_costs,
            Total_Sales: item.total_sales,
            Mstr_Country_Id: item.mstr_country_id,
            StatusName: item.statusname,
            ITEMTYPE_ID: item.itemtype_id,
            SupplierName: item.suppliername,
            Mstr_VATTaxID: item.mstr_vattaxid,
            TotalGrossAmount: item.totalgrossamount,
          };
          return result;
        });
        return transformed;
      },
      providesTags: ['ChargeLine'],
    }),
    createChargeLine: builder.mutation<any, Partial<ChargeLineEntity>>({
      query: (chargeLine) => ({
        url: '/WosChargeLine/CreateChargeLineWOS',
        method: 'POST',
        body: chargeLine,
      }),
      invalidatesTags: ['ChargeLine'],
    }),
    updateChargeLine: builder.mutation<any, Partial<ChargeLineEntity>>({
      query: (chargeLine) => ({
        url: '/WosChargeLine/UpdateChargeLineWOS',
        method: 'PUT',
        body: chargeLine,
      }),
      invalidatesTags: ['ChargeLine'],
    }),
    deleteChargeLine: builder.mutation<any, Partial<ChargeLineEntity>>({
      query: (chargeLine) => ({
        url: '/WosChargeLine/MarkDeleteWoserviceChargeLine',
        method: 'PUT',
        body: chargeLine,
      }),
      invalidatesTags: ['ChargeLine'],
    }),
      copyChargeLine: builder.mutation<any, Partial<ChargeLineEntity>>({
      query: (chargeLine) => ({
        url: '/WosChargeLine/CopyChargeLineWOS',
        method: 'POST',
        body: chargeLine,
      }),
      invalidatesTags: ['ChargeLine'],
    }),
  }),
});

export const {
  useGetChargeLinesByServiceIdQuery,
  useCreateChargeLineMutation,
  useUpdateChargeLineMutation,
  useDeleteChargeLineMutation,
  useCopyChargeLineMutation,
} = chargeLineAPI;